import { Search } from "@outschool/business-rules";
import {
  AutoAppliedFilterType,
  AutoAppliedFilters,
} from "@outschool/gql-backend-generated";
import {
  SearchListingsQuery,
  SearchListingsQueryVariables,
  SearchObjective,
} from "@outschool/gql-frontend-generated";
import { QueryHookOptions, QueryResult } from "@outschool/ui-apollo";
import { useExpiringValue } from "@outschool/ui-utils";
import { camelCase, isNumber, omit, random, uniqBy } from "lodash";
import { useCallback, useMemo, useState } from "react";

import usePaginatedQuery from "../hooks/usePaginatedQuery";
import searchListingsQuery from "./graphql/searchListingsQuery";

export type DisableAutoApplyFilters = (
  disableFilters: AutoAppliedFilterType[]
) => void;

export function useSearchQuery(
  options: QueryHookOptions<
    SearchListingsQuery,
    SearchListingsQueryVariables
  > & {
    usePromotedPaging?: boolean;
    includeMatchingSections?: boolean;
    source?: boolean;
    referrer?: string;
  },
  skipOtherListings: boolean = false
) {
  const [disabledAutoApplyFilters, setDisabledAutoApplyFilters] = useState<
    AutoAppliedFilterType[]
  >([]);
  const getItems = useCallback((result: SearchListingsQuery) => {
    const esResult = result as SearchListingsQuery;
    return esResult?.searchListings?.listings ?? [];
  }, []);
  const getResult = useCallback(
    (
      previousResult: SearchListingsQuery,
      fetchMoreResult: SearchListingsQuery,
      items: SearchListingsQuery["searchListings"]["listings"]
    ) => {
      const uniqueItems = uniqBy(items, listing => listing.activity.uid);
      const esPrev = previousResult as SearchListingsQuery;
      const esNext = fetchMoreResult as SearchListingsQuery;

      return {
        ...previousResult,
        ...fetchMoreResult,
        searchListings: {
          ...esPrev.searchListings,
          ...esNext.searchListings,
          listings: uniqueItems,
        },
      };
    },
    []
  );
  const getFetchMoreVariables = useCallback(
    (
      result: QueryResult<SearchListingsQuery, SearchListingsQueryVariables>
    ) => {
      const esResult = result as QueryResult<
        SearchListingsQuery,
        SearchListingsQueryVariables
      >;
      return {
        cursor: esResult.data?.searchListings.cursor,
        // @ts-ignore TS(2345): Argument of type 'SearchListingsQuery | undefined'... Remove this comment to see the full error message
        offset: getItems(result.data).length,
      };
    },
    [getItems]
  );

  const hasMoreItems = useCallback(
    (data: SearchListingsQuery) => {
      const esResult = data as SearchListingsQuery;
      return options.usePromotedPaging
        ? getItems(data).length > 0 &&
            // @ts-ignore TS(2531): Object is possibly 'null'.
            esResult?.searchListings?.total >
              // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
              options.variables?.offset + options.variables.limit
        : Boolean(esResult?.searchListings?.cursor);
    },
    [
      getItems,
      options.usePromotedPaging,
      options.variables?.offset,
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      options.variables.limit,
    ]
  );

  // The backend needs a seed for randomization purposes on no query searches
  const [seed] = useExpiringValue(
    "randomized_search_key",
    random(1000000),
    30 * 60 * 1000
  );

  const learnerPlanGoalUid =
    options.variables?.[Search.SCHEDULE_PLANNER_TRACKER];

  const searchFilters = {
    // Some things exist on search params but we don't want to send them to the backend.
    ...omit(options.variables?.searchFilters, [
      Search.SCHEDULE_PLANNER_TRACKER,
    ]),
    /**
     *
     * Added a check to allow for querying unavailable classes for the
     * use case of outschool subscriptions where self-paced classes
     * have no sections and the classes aren't open for enrollments.
     *
     */
    ...("undefined" === typeof options.variables?.searchFilters?.isAvailable
      ? { isAvailable: true }
      : {}),
  };

  const exactResult = usePaginatedQuery(
    searchListingsQuery,
    {
      ...options,
      variables: {
        ...options.variables!,
        searchFilters: {
          ...searchFilters,
          includeSelfPaced: true,
          seed,
        },
        referrer: options.referrer,
        includeMatchingSections: options.includeMatchingSections,
        source: options.source,
        searchObjective: SearchObjective.AvailableListings,
        disableAutoApplyFilterTypes: disabledAutoApplyFilters,
        learnerPlanGoalUid,
      },
    },
    getItems,
    getResult,
    getFetchMoreVariables,
    hasMoreItems
  );
  // Unavailable classes query
  const otherResult = usePaginatedQuery(
    searchListingsQuery,
    {
      ...options,
      variables: {
        ...options.variables!,
        searchFilters: {
          ...searchFilters,
          includeSelfPaced: true,
          isAvailable: false,
        },
        promotedExperimentGroup: undefined,
        promotedRankingsEnabled: false,
        searchObjective: SearchObjective.UnavailableListings,
        disableAutoApplyFilterTypes: disabledAutoApplyFilters,
      },
      skip:
        skipOtherListings ||
        exactResult?.data === undefined ||
        (exactResult?.hasMore ?? true),
    },
    getItems,
    getResult,
    getFetchMoreVariables,
    hasMoreItems
  );

  const fetchMore = useMemo(
    () =>
      exactResult?.hasMore
        ? exactResult?.fetchMore
        : otherResult?.hasMore
        ? otherResult?.fetchMore
        : () => {},
    [
      exactResult?.hasMore,
      exactResult?.fetchMore,
      otherResult?.hasMore,
      otherResult?.fetchMore,
    ]
  );

  const getTotalFromResult = (result: Pick<QueryResult, "data">): number => {
    const total = result?.data?.searchListings?.total;

    if (isNumber(total)) {
      return total;
    }

    // @ts-ignore TS(2322): Type 'null' is not assignable to type 'number'.
    return null;
  };

  return {
    autoAppliedFilters:
      exactResult?.data?.searchListings?.autoAppliedFilters ?? {},
    // @ts-ignore TS(2345): Argument of type '{ searchListings: { listings: { ... Remove this comment to see the full error message
    exactListings: getItems(exactResult.data),
    exactTotal: getTotalFromResult(exactResult),
    // @ts-ignore TS(2345): Argument of type '{ searchListings: { listings: { ... Remove this comment to see the full error message
    otherListings: getItems(otherResult.data),
    otherTotal: getTotalFromResult(otherResult),
    loading: exactResult?.loading ?? false,
    hasMore: (exactResult?.hasMore || otherResult?.hasMore) ?? true,
    exactHasMore: exactResult?.hasMore ?? true,
    isFetchingMore:
      (exactResult?.isFetchingMore ||
        otherResult?.loading ||
        otherResult?.isFetchingMore) ??
      false,
    fetchMore,
    error: exactResult?.error ?? otherResult?.error,
    setDisabledAutoApplyFilters,
    disabledAutoApplyFilters,
  };
}

export function setDisabledAutoApplyFilter({
  disableFilterType,
  autoAppliedFilters,
  disabledAutoApplyFilters,
  setDisabledAutoApplyFilters,
}: {
  disableFilterType: AutoAppliedFilterType;
  autoAppliedFilters: AutoAppliedFilters;
  disabledAutoApplyFilters: AutoAppliedFilterType[];
  setDisabledAutoApplyFilters: DisableAutoApplyFilters;
}) {
  const disableFilterTypeKey = camelCase(disableFilterType);
  // are auto applied filters active for this filter type?
  const hasAutoAppliedFilters =
    autoAppliedFilters?.[disableFilterTypeKey as keyof AutoAppliedFilters]
      ?.length &&
    (autoAppliedFilters?.[disableFilterTypeKey as keyof AutoAppliedFilters]
      ?.length ?? 0) > 0;
  // is this auto appplied filter enabled?
  const canFilterTypeBeDisabled =
    !disabledAutoApplyFilters?.includes(disableFilterType);
  if (
    setDisabledAutoApplyFilters &&
    hasAutoAppliedFilters &&
    canFilterTypeBeDisabled
  ) {
    setDisabledAutoApplyFilters([
      ...disabledAutoApplyFilters,
      disableFilterType,
    ]);
  }
}
